import { useState } from "react";
import PageWrapper from "../../components/PageWrapper";
import { useTranslation } from "react-i18next";
import Loader, { LOADER_SIZES } from "../../components/Loader";
import ErrorBox from "../../components/ErrorBox";
import axiosClient from "../../services/axiosClient";
import { useParams } from "react-router-dom";
import { useEffect } from "react";
import TextDiv from "../../components/TextDiv";
import HotdropdownV2 from "../../components/HotdropdownV2";

import SectionDivider from "../../components/SectionDivider";
import {
  CollapsibleContent,
  CollapsibleGroup,
  CollapsibleHeader,
  CollapsibleLabel,
  CollapsibleList,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "../../components/CollapsibleGroup";
import arrowIcon from "../../assets/previous_icon.png";
import { getAllEducationLevels } from "../../services/educationLevelServices";
import CustomInput from "../../components/CustomInput";
import useDebounce from "../../utils/useDebounce";
import InformationBox from "../../components/InformationBox";
import { formatDate } from "../../utils/toDate";

const StudentSupplies = () => {
  const { t } = useTranslation("general");
  const [student, setStudent] = useState(null);
  const [loadingStudent, setLoadingStudent] = useState(false);
  const [errorStudent, setErrorStudent] = useState(false);
  const { id } = useParams();

  const [selectedSupplie, setSelectedSupplie] = useState("uniform");

  const supplieOptions = [
    { id: "books", title: t("books") },
    { id: "uniform", title: t("uniform") },
  ];

  async function getStudent() {
    try {
      setLoadingStudent(true);
      const response = await axiosClient.get(`/student/${id}`);
      setStudent(response.data);
    } catch (error) {
      if (error?.response?.status === 404) {
        setErrorStudent("studentNotFound");
        return;
      }
      setErrorStudent("errorGettingStudent");
    } finally {
      setLoadingStudent(false);
    }
  }

  useEffect(() => {
    getStudent();
  }, []);

  const canShowSupplie = !loadingStudent && student;

  return (
    <PageWrapper title={t("studentSupplies")} withBackButton>
      {errorStudent && <ErrorBox>{t(errorStudent)}</ErrorBox>}

      <div className="u-margin--bottom_500 o-column o-column--gap-size_300 u-margin--top_600">
        <TextDiv
          title="studentName"
          value={student?.fullName}
          divClasses="o-row o-row--gap-size_300 o-row--vertical-align_center"
          pClasses="u-min-width-specific_150"
        />

        <TextDiv
          title="studentId"
          value={student?.studentCode}
          divClasses="o-row o-row--gap-size_300 o-row--vertical-align_center"
          pClasses="u-min-width-specific_150"
        />
      </div>

      <div className="o-column o-column--gap-size_200 u-width_full-width u-margin--top_700">
        <label>{t("supplieType")}</label>
        <div>
          <HotdropdownV2
            initOptions={supplieOptions}
            selected={supplieOptions.find(
              (belonging) => belonging.id === selectedSupplie
            )}
            setSelected={(selected) => {
              setSelectedSupplie(selected.id);
            }}
            optionsName="id"
          />
        </div>
        {canShowSupplie && selectedSupplie === "books" && (
          <SupplieBooks student={student} />
        )}

        {canShowSupplie && selectedSupplie === "uniform" && (
          <SupplieUniform student={student} />
        )}
        {loadingStudent && <Loader size={LOADER_SIZES.MEDIUM} />}
      </div>
    </PageWrapper>
  );
};

function SupplieBooks({ student }) {
  const { t } = useTranslation("general");
  const [educationLevels, setEducationLevels] = useState([]);
  const [selectedAcademicLevel, setSelectedAcademicLevel] = useState({
    title: t("selectAll"),
    value: null,
  });
  const [searchQuery, setSearchQuery] = useState("");
  const searchValue = useDebounce(searchQuery);
  const [loadingBooks, setLoadingBooks] = useState(false);
  const [booksData, setBooksData] = useState([]);
  const [errorBooks, setErrorBooks] = useState(false);

  // Book headers configuration
  const bookHeaders = [
    { key: "name", label: "bookName" },
    { key: "productSubType", label: "bookType" },
    {
      key: "subclass",
      label: "subclass",
    },
    {
      key: "quantity",
      label: "quantity",
    },
    {
      key: "Date",
      label: "Date",
    },
  ];

  const getAllBooks = async () => {
    const organizationId = localStorage.getItem("organizationId");
    if (!organizationId) return;

    try {
      setErrorBooks(false);
      setLoadingBooks(true);
      setBooksData([]);
      const response = await axiosClient.get(
        `/student/forProduct/${student.id}`,
        {
          params: {
            name: searchValue,
            classId:
              selectedAcademicLevel.value === null
                ? null
                : selectedAcademicLevel.title,
            studentCode: student.studentCode,
          },
        }
      );

      if (response.data?.classes) {
        setBooksData(response.data.classes);
      }
    } catch (err) {
      setErrorBooks("errorGettingBooks");
    } finally {
      setLoadingBooks(false);
    }
  };

  const fetchEducationLevels = async () => {
    try {
      const response = await getAllEducationLevels();

      const transformedData = (response.data.classes || []).map((level) => ({
        title: level.name,
        value: level.id,
        price: level.price,
        schoolSubClasses:
          level.schoolSubClasses?.map((subClass) => ({
            title: subClass.name,
            value: subClass.id,
          })) || [],
      }));

      setEducationLevels(transformedData);

      return {
        status: response.status || 200,
        data: {
          classes: transformedData,
          totalPages: response.data.totalPages,
          totalRecords: response.data.totalRecords,
        },
      };
    } catch (error) {
      console.error("Error fetching education levels:", error);
      return {
        status: 500,
        data: { classes: [], totalPages: 1, totalRecords: 0 },
      };
    }
  };

  const handleAcademicLevelChange = (selected) => {
    setSelectedAcademicLevel(selected);
  };

  const isThereBooks = !loadingBooks && booksData.length > 0 && !errorBooks;

  useEffect(() => {
    getAllBooks();
  }, [searchValue, selectedAcademicLevel.value]);

  useEffect(() => {
    fetchEducationLevels();
  }, []);

  // const transformedClasses = booksData?.map((classItem) => {
  //   const sells = [];

  //   // Iterate through subClasses
  //   classItem.subClasses.forEach((subClass) => {
  //     // Iterate through students
  //     subClass.students.forEach((student) => {
  //       // Iterate through items
  //       student.items.forEach((item) => {
  //         item.sellDetails.forEach(sell => {
  //           sells.push({
  //             date: sell.date,
  //             quantity: sell.quantity,
  //             productName: item.product.name,
  //             subClassName: subClass.name,
  //             productSubType: item.product.productSubType,
  //           })
  //         })
  //       });
  //     });
  //   });

  //   return {
  //     name: classItem.name,
  //     sells: sells,
  //   };
  // });
  // console.log({ transformedClasses });

  console.log({ booksData });
  return (
    <>
      <div className="o-column-row-switcher o-column--vertical-align_start o-row--gap-size_300">
        <div className="o-column o-column--gap-size_200 u-margin--top_400">
          <label>{t("academicLevel")}</label>
          <div>
            <HotdropdownV2
              initOptions={[
                ...educationLevels,
                { title: t("selectAll"), value: null },
              ]}
              selected={selectedAcademicLevel.title || ""}
              setSelected={handleAcademicLevelChange}
              page="students"
              getData={fetchEducationLevels}
              optionsName="classes"
              flex
            />
          </div>
        </div>

        <div className="o-column o-column--gap-size_200 u-margin--top_400 o-column--vertical-align_start">
          <label>{t("bookName")}</label>
          <CustomInput
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            flex
          />
        </div>
      </div>
      {errorBooks && !loadingBooks && <ErrorBox>{t(errorBooks)}</ErrorBox>}
      <div className="App o-column o-column--gap-size_400 u-padding--bottom_500 u-margin--top_500">
        {!loadingBooks && booksData?.length === 0 && !errorBooks && (
          <InformationBox>{t("noSuppliesFound")}</InformationBox>
        )}
        {loadingBooks && <Loader size={LOADER_SIZES.MEDIUM} />}
        {isThereBooks && (
          <>
            <SectionDivider title={t("theBooks")} />
            <CollapsibleList>
              {booksData?.map((schoolClass) => (
                <CollapsibleGroup key={schoolClass.id} defaultCollapsed={false}>
                  <div className="c-fabric-block">
                    <CollapsibleHeader icon={arrowIcon}>
                      <CollapsibleLabel
                        label={"academicLevel"}
                        value={schoolClass.name}
                      />
                    </CollapsibleHeader>
                    <CollapsibleContent>
                      <Table>
                        <TableHeader>
                          {bookHeaders.map((header) => (
                            <TableHeaderCell
                              key={header.key}
                              label={header.label}
                            />
                          ))}
                        </TableHeader>
                        <TableBody>
                          {schoolClass.sells?.map((item) => (
                            <TableRow key={item.id}>
                              {console.log({ item })}
                              <TableCell>{item.productName}</TableCell>
                              <TableCell>{t(item.productSubType)}</TableCell>
                              <TableCell>{item.studentSubClass}</TableCell>
                              <TableCell>{item.quantity}</TableCell>
                              <TableCell>{formatDate(item.date)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CollapsibleContent>
                  </div>
                </CollapsibleGroup>
              ))}
            </CollapsibleList>
          </>
        )}
      </div>
    </>
  );
}

function SupplieUniform({ student }) {
  const { t } = useTranslation("general");
  const [uniformName, setUniformName] = useState("");
  const [sizeName, setSizeName] = useState("");
  const uniformNameValue = useDebounce(uniformName, 500);
  const sizeNameValue = useDebounce(sizeName, 500);
  const [loadingUniform, setLoadingUniform] = useState(false);
  const [uniformsData, setUniformsData] = useState([]);
  const [errorUniform, setErrorUniform] = useState(false);

  // Configuration
  const uniformHeaders = [
    { key: "uniformName", label: "uniformName" },
    { key: "size", label: "size" },
    {
      key: "subclass",
      label: "subclass",
    },
    { key: "quantity", label: "quantity" },
    { key: "Date", label: "Date" },
  ];

  const getAllUniforms = async () => {
    const organizationId = localStorage.getItem("organizationId");
    if (!organizationId) return;

    try {
      setErrorUniform(false);
      setLoadingUniform(true);
      setUniformsData([]);
      const response = await axiosClient.get(
        `/student/forProductRuler/${student.id}`,
        {
          params: {
            organizationId,
            limit: 10000,
            name: uniformNameValue,
            size: sizeNameValue,
          },
        }
      );

      if (response.status === 200 && response.data) {
        setUniformsData(response.data.classes);
      }
    } catch (err) {
      setErrorUniform("errorGettingUniforms");
    } finally {
      setLoadingUniform(false);
    }
  };

  useEffect(() => {
    getAllUniforms();
  }, [uniformNameValue, sizeNameValue]);

  const isThereUniforms =
    !loadingUniform && uniformsData.length > 0 && !errorUniform;
  return (
    <>
      <div className="o-column-row-switcher o-column--vertical-align_start o-row--gap-size_300">
        <div className="o-column o-column--gap-size_200 u-margin--top_400 o-column--vertical-align_start">
          <label>{t("uniformName")}</label>
          <CustomInput
            value={uniformName}
            onChange={(e) => setUniformName(e.target.value)}
            flex
          />
        </div>
        <div className="o-column o-column--gap-size_200 u-margin--top_400 o-column--vertical-align_start">
          <label>{t("size")}</label>
          <CustomInput
            value={sizeName}
            onChange={(e) => setSizeName(e.target.value)}
            flex
          />
        </div>
      </div>
      {errorUniform && <ErrorBox>{t(errorUniform)}</ErrorBox>}
      <div className="App o-column o-column--gap-size_400 u-padding--bottom_500 u-margin--top_500">
        {loadingUniform && <Loader size={LOADER_SIZES.MEDIUM} />}
        {!loadingUniform && uniformsData.length === 0 && !errorUniform && (
          <InformationBox>{t("noSuppliesFound")}</InformationBox>
        )}
        {isThereUniforms && (
          <>
            <SectionDivider title={t("uniforms")} />
            <CollapsibleList>
              {uniformsData.map((schoolClass) => (
                <CollapsibleGroup key={schoolClass} defaultCollapsed={true}>
                  <div className="c-fabric-block">
                    <CollapsibleHeader icon={arrowIcon}>
                      <CollapsibleLabel
                        label={"academicLevel"}
                        value={schoolClass.name}
                      />
                    </CollapsibleHeader>

                    <CollapsibleContent>
                      <Table>
                        <TableHeader>
                          {uniformHeaders.map((header) => (
                            <TableHeaderCell
                              key={header.key}
                              label={header.label}
                            />
                          ))}
                        </TableHeader>
                        <TableBody>
                          {schoolClass?.sells?.map((item) => (
                            <TableRow key={item.id}>
                              <TableCell>{item.productName}</TableCell>
                              <TableCell>{item.productSize}</TableCell>
                              <TableCell>{item.studentSubClass}</TableCell>
                              <TableCell>{item.quantity}</TableCell>
                              <TableCell>{formatDate(item.date)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CollapsibleContent>
                  </div>
                </CollapsibleGroup>
              ))}
            </CollapsibleList>
          </>
        )}
      </div>
    </>
  );
}

export default StudentSupplies;
